import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { useEffect, useState } from "react";
import { Shield, Loader2, CheckCircle2, ExternalLink, Lock, Download } from "lucide-react";
import { useTheme } from "@/lib/theme-context";
import { z } from "zod";
import { decryptUrl } from "@/lib/safelink-crypto";
import { logSafelink } from "@/lib/safelink-log";
const search = z.object({ url: z.string().optional(), t: z.string().optional(), d: z.coerce.number().optional() });
export const Route = createFileRoute("/safelink")({
  validateSearch: search,
  head: () => ({
    meta: [
      { title: "رابط محمي — Rawan UI" },
      { name: "description", content: "صفحة حماية الروابط لضمان أمان الزائر." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "رابط محمي" },
    ],
    links: [{ rel: "canonical", href: "/safelink" }],
  }),
  component: SafelinkPage,
});
function SafelinkPage() {
  const { url, t: token, d } = Route.useSearch();
  const isDownload = d === 1;
  const { dir, language } = useTheme();
  const isAr = dir === "rtl";
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  const [count, setCount] = useState(10);
  const [ready, setReady] = useState(false);
  const [target, setTarget] = useState<string>("");
  const [decodeError, setDecodeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (token) {
          const decoded = await decryptUrl(token);
          if (!cancelled) {
            setTarget(decoded);
            logSafelink({ source: "safelink", status: "success" });
          }
        } else if (url) {
          if (!cancelled) setTarget(url);
        }
      } catch {
        if (!cancelled) {
          setDecodeError((language === "ar" ? "\u062a\u0639\u0630\u0631 \u0641\u0643 \u062a\u0634\u0641\u064a\u0631 \u0627\u0644\u0631\u0627\u0628\u0637." : "Could not decode the secure link."));
          logSafelink({ source: "safelink", status: "decrypt-failed" });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [token, url, isAr]);

  useEffect(() => {
    if (count <= 0) { setReady(true); return; }
    const i = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(i);
  }, [count]);
  const progress = ((10 - count) / 10) * 100;
  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12 max-w-2xl grid place-items-center">
        <div className="w-full bg-card border border-border rounded-2xl p-8 text-center shadow-card animate-scale-in">
          <div className="grid place-items-center w-16 h-16 mx-auto rounded-2xl animate-pulse-glow" style={{ background: "var(--gradient-primary)" }}>
            <Shield className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-extrabold mt-4">{t("جارٍ التحقق من الرابط…", "Verifying link…")}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {t("نقوم بفحص الرابط لحمايتك من المحتوى الضار.", "We're checking the link to protect you from malicious content.")}
          </p>
          {/* Progress */}
          <div className="mt-6">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full transition-all duration-1000" style={{ width: `${progress}%`, background: "var(--gradient-primary)" }} />
            </div>
            <div className="flex items-center justify-center gap-2 mt-3 text-sm text-muted-foreground">
              {ready ? (
                <><CheckCircle2 className="h-4 w-4 text-primary" /> {t("جاهز!", "Ready!")}</>
              ) : (
                <><Loader2 className="h-4 w-4 animate-spin" /> {t(`الرجاء الانتظار ${count} ث`, `Please wait ${count}s`)}</>
              )}
            </div>
          </div>
          {/* CTA */}
          <div className="mt-8">
            {decodeError ? (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg p-3 inline-block">
                {decodeError}
              </div>
            ) : ready && target ? (
              isDownload ? (
                <button
                  onClick={() => window.open(target, "_blank", "noopener,noreferrer")}
                  className="btn-primary inline-flex items-center gap-2 text-base"
                >
                  <Download className="h-4 w-4" />
                  {t("بدء التحميل", "Start download")}
                </button>
              ) : (
                <a
                  href={target}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="btn-primary inline-flex items-center gap-2 text-base"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t("متابعة إلى الرابط", "Continue to link")}
                </a>
              )
            ) : (
              <button disabled className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-muted text-muted-foreground cursor-not-allowed">
                <Lock className="h-4 w-4" /> {t("مقفل", "Locked")}
              </button>
            )}
          </div>
          {/* Ad slot placeholder */}
          <div className="mt-8 p-4 bg-muted/50 rounded-xl border border-dashed border-border text-xs text-muted-foreground">
            {t("مساحة إعلانية — ضع كود إعلان هنا", "Ad space — place your ad code here")}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}